import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import type { CallLogView, ChatView } from '@olmt/shared'
import { api } from '../lib/api'
import { canCallBack, callLogSummary } from '../lib/calls'
import { chatAvatarUrl, chatTitle } from '../lib/chat'
import { startOrJoinCall } from '../lib/data'
import { formatListStamp } from '../lib/time'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { Avatar } from '../components/Avatar'
import { IconButton } from '../components/IconButton'
import { Spinner } from '../components/Spinner'
import { ArrowLeftIcon, PhoneIcon } from '../components/Icons'

/**
 * The call log (W6) — every call in the viewer's chats, newest first (`GET /api/calls`), on a page
 * of its own the way the account screen is: full-page on a phone, and back to the chats from the
 * header it wears.
 *
 * The list is flat, like the chat list beside it (neither groups by day), and it is read-only
 * except for one thing: a row rings its chat back. That is the same flow the thread's header uses
 * (`startOrJoinCall`) — a call already running there is joined, a call this device handed to the
 * native host comes back to the front — so a call-back is never a second call.
 *
 * The rules a row follows are `lib/calls.ts`'s: which state line it says and whether it may ring at
 * all. This file only paints them.
 */
export function CallLogPage() {
  const navigate = useNavigate()
  const t = useT()
  const meId = useAuthStore((state) => state.user?.id ?? null)
  // The live chats: a row's name and photo are read from the store when it has them, and whether
  // the row can still ring its chat is a question about the store's membership, not the log's.
  const chats = useChatStore((state) => state.chats)

  const [entries, setEntries] = useState<CallLogView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // The api decided the order and the facts; this asks for the newest page and nothing else. A
    // late answer from a page the reader has left is dropped rather than painted.
    let live = true
    const load = async () => {
      try {
        const response = await api.callLog()
        if (!live) return
        setEntries(response.calls)
        setError(null)
      } catch {
        if (live) setError(t('calls.loadFailed'))
      } finally {
        if (live) setLoading(false)
      }
    }
    void load()
    return () => {
      live = false
    }
  }, [t])

  const callBack = async (entry: CallLogView) => {
    setError(null)
    try {
      await startOrJoinCall(entry.chat.id, entry.call.kind)
    } catch {
      setError(t('calls.failed'))
    }
  }

  return (
    <div className="app-shell">
      <header className="app-header flex items-center gap-2 border-b border-line bg-surface">
        <IconButton label={t('calls.backToChats')} onClick={() => void navigate('/')}>
          <ArrowLeftIcon size={22} />
        </IconButton>
        <h1 className="flex-1 text-title font-semibold text-ink">{t('calls.title')}</h1>
      </header>

      <main className="chat-scroll">
        <div className="room-column px-3 py-3">
          {error && (
            <p
              role="alert"
              className="mb-2 rounded-panel bg-danger/10 px-3 py-2 text-meta text-danger ring-1 ring-danger/25 ring-inset"
            >
              {error}
            </p>
          )}

          {loading && (
            <div className="flex justify-center py-8 text-muted">
              <Spinner label={t('calls.loading')} />
            </div>
          )}

          {/* A failed read says so instead of "no calls yet": an empty list and an unread list are
              not the same answer. */}
          {!loading && !error && entries.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-8 py-16 text-center">
              <span
                aria-hidden="true"
                className="mb-1 flex size-14 items-center justify-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/25 ring-inset"
              >
                <PhoneIcon size={26} />
              </span>
              <p className="text-body font-semibold text-ink">{t('calls.empty')}</p>
            </div>
          )}

          {!loading && entries.length > 0 && (
            <ul>
              {entries.map((entry) => (
                <CallLogRow
                  key={entry.call.id}
                  entry={entry}
                  chat={chats[entry.chat.id] ?? null}
                  meId={meId}
                  onCallBack={(row) => void callBack(row)}
                />
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  )
}

type CallLogRowProps = {
  entry: CallLogView
  /** The chat as the store has it now, or null when the viewer is no longer in it. */
  chat: ChatView | null
  meId: string | null
  onCallBack: (entry: CallLogView) => void
}

/**
 * One call. The row *is* the button — the thing that shows a call is the thing that rings it back,
 * the same shape the pinned bar follows (W5) — and the words inside it say what the call was:
 * missed in `danger`, its duration when it was answered, otherwise its direction.
 *
 * The name and photo come from the ticket's chat as the store knows it, falling back to the log's
 * own copy of the chat for anything the store has not heard of yet. A chat the viewer has left
 * keeps its row (the call still happened) but loses the button: a control that can only fail is
 * not a control.
 */
function CallLogRow({ entry, chat, meId, onCallBack }: CallLogRowProps) {
  const t = useT()
  const view = chat ?? entry.chat
  const title = chatTitle(view, meId)
  const summary = callLogSummary(entry)
  const callable = canCallBack(entry, chat, meId)

  const body = (
    <>
      <Avatar name={title} path={chatAvatarUrl(view, meId)} size={48} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-body font-semibold text-ink">
            <bdi>{title}</bdi>
          </span>
          <span className="numeric shrink-0 text-meta text-muted">{formatListStamp(entry.call.startedAt)}</span>
        </span>
        <span className={`mt-0.5 flex items-center gap-1.5 text-ui ${entry.missed ? 'text-danger' : 'text-muted'}`}>
          <DirectionArrow direction={entry.direction} />
          <span className="truncate">{t(summary.key, summary.vars)}</span>
        </span>
      </span>
      {/* What the row does, in the row's own words: the accessible name is the call ("Nadia",
          "missed call") plus this, so a screen reader hears the action without losing the state. */}
      {callable && <span className="sr-only">{t('calls.callBack')}</span>}
    </>
  )

  return (
    <li className="chat-row">
      {callable ? (
        <button
          type="button"
          onClick={() => onCallBack(entry)}
          className="flex min-h-[72px] w-full items-center gap-3 px-4 py-2.5 text-start transition-colors duration-quick hover:bg-surface2/50"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-h-[72px] items-center gap-3 px-4 py-2.5">{body}</div>
      )}
    </li>
  )
}

/**
 * The direction arrow: a call the viewer placed points away (up-right) and one they received points
 * back (down-left), which is the one arrow glyph at two fixed angles. Under RTL the horizontal axis
 * flips, so the glyph is mirrored rather than re-angled — `rtl:-scale-x-100` follows the document's
 * direction the way a logical utility follows it. Decorative: the state words beside it already say
 * what the call was.
 */
function DirectionArrow({ direction }: { direction: CallLogView['direction'] }) {
  return (
    <span aria-hidden="true" className="flex shrink-0 rtl:-scale-x-100">
      <ArrowLeftIcon size={14} className={direction === 'outgoing' ? 'rotate-[135deg]' : '-rotate-[45deg]'} />
    </span>
  )
}
